import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Ship,
  Compass,
  Map,
  Trophy,
  Star,
  Target,
  CheckCircle,
  Circle,
  PlayCircle,
  Lock,
  Zap,
  ShieldCheck,
  Hourglass,
  Sparkles,
  XCircle
} from 'lucide-react';
import { differenceInDays, parseISO } from 'date-fns';
import { chapterLabel, chapterOrder } from '../utils/chapterLabel';

const ProgressionPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  
  const [progression, setProgression] = useState(null);
  const [userMilestones, setUserMilestones] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [milestoneDefinitions, setMilestoneDefinitions] = useState([]);
  const [declareDialog, setDeclareDialog] = useState({ open: false, milestone: null });
  const [proofText, setProofText] = useState('');
  const [declaring, setDeclaring] = useState(false);
  const [quizDialog, setQuizDialog] = useState({ open: false, chapterId: null, quiz: null });
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [pendingLesson, setPendingLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [progRes, chapRes, msDefRes, userMsRes] = await Promise.all([
        api.get('/user/progression'),
        api.get('/chapters'),
        api.get('/milestones/definitions'),
        api.get('/user/milestones')
      ]);
      setProgression(progRes.data);
      setChapters([...(chapRes.data || [])].sort((a, b) => chapterOrder(a) - chapterOrder(b)));
      setMilestoneDefinitions(msDefRes.data);
      setUserMilestones(userMsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const openLesson = (chapter) => {
    // On ouvre l'onglet immédiatement (sinon le navigateur bloque le popup) puis on trace
    const tab = window.open(chapter.lesson_url, '_blank', 'noopener');
    if (!tab) toast.error('Autorise les popups pour ouvrir la leçon');
    setPendingLesson(chapter);
    api.post(`/user/progression/chapter/${chapter.id}/open-lesson`).catch(() => {});
  };

  const completeChapter = async (chapterId) => {
    setQuizLoading(true);
    try {
      const quizRes = await api.get(`/user/progression/chapter/${chapterId}/quiz`);
      if (quizRes.data.quiz_required) {
        setQuizDialog({ open: true, chapterId, quiz: quizRes.data });
        setQuizAnswer(null);
        return;
      }
      const res = await api.post(`/user/progression/chapter/${chapterId}/complete`, {});
      toast.success(res.data.message);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setQuizLoading(false);
    }
  };

  const submitQuiz = async () => {
    if (quizAnswer === null) {
      toast.error('Choisis une réponse');
      return;
    }
    setQuizLoading(true);
    try {
      const res = await api.post(`/user/progression/chapter/${quizDialog.chapterId}/complete`, {
        quiz_id: quizDialog.quiz.quiz_id,
        answer_index: quizAnswer,
      });
      toast.success(res.data.message);
      setQuizDialog({ open: false, chapterId: null, quiz: null });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
      setQuizDialog({ open: false, chapterId: null, quiz: null });
    } finally {
      setQuizLoading(false);
    }
  };

  const uncompleteChapter = async (chapterId) => {
    try {
      const res = await api.post(`/user/progression/chapter/${chapterId}/uncomplete`);
      toast.success(res.data.message);
      fetchAll();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const completeMilestone = async (milestone) => {
    if ((milestone.verification || 'coach') === 'auto') {
      try {
        const res = await api.post(`/user/milestones/${milestone.id}/complete`);
        toast.success(res.data.message);
        fetchAll();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
      }
      return;
    }
    setProofText('');
    setDeclareDialog({ open: true, milestone });
  };

  const submitDeclaration = async () => {
    const milestone = declareDialog.milestone;
    if (!milestone) return;
    if (proofText.trim().length < 80) {
      toast.error('Ton justificatif doit faire au moins 80 caractères (dates, montants, interlocuteurs).');
      return;
    }
    setDeclaring(true);
    try {
      const res = await api.post(`/user/milestones/${milestone.id}/complete`, { proof_text: proofText.trim() });
      const status = res.data.status;
      if (status === 'approved') {
        toast.success(res.data.message);
        setDeclareDialog({ open: false, milestone: null });
      } else if (status === 'rejected') {
        toast.error(res.data.ai_verdict?.reason || res.data.message);
      } else {
        toast.success(res.data.message);
        setDeclareDialog({ open: false, milestone: null });
      }
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la déclaration');
    } finally {
      setDeclaring(false);
    }
  };

  const uncompleteMilestone = async (milestoneId) => {
    try {
      const res = await api.post(`/user/milestones/${milestoneId}/uncomplete`);
      toast.success(res.data.message);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const completedChapters = progression?.completed_chapters || [];
  const currentChapter = progression?.current_chapter || 1;
  const maxCompletedIndex = chapters.reduce(
    (max, c, i) => (completedChapters.includes(c.id) ? Math.max(max, i) : max),
    -1
  );
  const unlockedIndex = maxCompletedIndex + 1;
  const totalChapters = chapters.length || 15;
  const progressPercent = totalChapters > 0 ? (completedChapters.length / totalChapters) * 100 : 0;
  
  const daysOnJourney = progression?.started_at 
    ? differenceInDays(new Date(), parseISO(progression.started_at))
    : 0;

  const completedMilestonesCount = userMilestones.filter(m => m.completed).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Ship className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement de ton odyssée...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="progression-page">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold flex items-center gap-2">
                <Ship className="w-6 h-6 text-primary" />
                Mon Odyssée Immobilière
              </h1>
              <p className="text-sm text-muted-foreground">Ton parcours vers la liberté financière</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Retour de leçon : validation en un geste au retour de ThriveCart */}
        {pendingLesson && !completedChapters.includes(pendingLesson.id) && (
          <div
            className="mb-6 rounded-xl border border-primary/40 bg-primary/5 p-4 flex items-start sm:items-center gap-3 flex-wrap"
            data-testid="lesson-return-banner"
          >
            <PlayCircle className="w-5 h-5 text-primary shrink-0 mt-0.5 sm:mt-0" />
            <p className="text-sm flex-1 min-w-[180px]">
              Tu as ouvert <span className="font-semibold">{chapterLabel(pendingLesson)}</span>.
              Tu l&apos;as terminé ?
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                className="bg-green-500 hover:bg-green-600"
                onClick={() => { completeChapter(pendingLesson.id); setPendingLesson(null); }}
                data-testid="lesson-return-validate"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Oui, valider
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPendingLesson(null)} data-testid="lesson-return-dismiss">
                Pas encore
              </Button>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-0 shadow-md bg-gradient-to-br from-primary/10 to-background">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-primary">{completedChapters.length}</p>
              <p className="text-xs text-muted-foreground">Chapitres complétés</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-amber-500/10 to-background">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-500">{Math.round(progressPercent)}%</p>
              <p className="text-xs text-muted-foreground">Progression</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-blue-500/10 to-background">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-500">{daysOnJourney}</p>
              <p className="text-xs text-muted-foreground">Jours de voyage</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-gradient-to-br from-green-500/10 to-background">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-500">{completedMilestonesCount}</p>
              <p className="text-xs text-muted-foreground">Jalons atteints</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Progress Bar */}
        <Card className="mb-8 border-0 shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-primary/20 via-amber-500/20 to-green-500/20 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                <Map className="w-5 h-5 text-primary" />
                Progression du parcours
              </h2>
              <Badge variant="outline" className="bg-primary/10 border-primary/30">
                {completedChapters.length} / {totalChapters} chapitres
              </Badge>
            </div>
            <div className="h-4 bg-secondary rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-primary via-amber-500 to-green-500"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>Début</span>
              <span>Liberté financière</span>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Chapters Timeline */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Compass className="w-5 h-5 text-primary" />
                  Les {totalChapters} étapes du voyage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />
                    
                    <div className="space-y-4">
                      {chapters.map((chapter, index) => {
                        const isCompleted = completedChapters.includes(chapter.id);
                        const isCurrent = chapter.id === currentChapter;
                        const isLocked = index > unlockedIndex && !isCompleted;
                        
                        return (
                          <motion.div
                            key={chapter.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className={`relative flex items-start gap-4 p-4 rounded-lg transition-all ${
                              isCompleted 
                                ? 'bg-green-500/10 border border-green-500/30' 
                                : isCurrent 
                                  ? 'bg-primary/10 border border-primary/30' 
                                  : isLocked
                                    ? 'bg-secondary/30 opacity-60'
                                    : 'bg-secondary/50 hover:bg-secondary/70'
                            }`}
                          >
                            {/* Timeline dot */}
                            <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center text-xl ${
                              isCompleted 
                                ? 'bg-green-500 text-white' 
                                : isCurrent 
                                  ? 'bg-primary text-white animate-pulse' 
                                  : 'bg-secondary'
                            }`}>
                              {isCompleted ? <CheckCircle className="w-6 h-6" /> : isLocked ? <Lock className="w-5 h-5" /> : chapter.icon}
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <h3 className="font-semibold break-words">
                                  {chapterLabel(chapter)}
                                </h3>
                                {isCompleted && (
                                  <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                                    Complété
                                  </Badge>
                                )}
                                {isCurrent && !isCompleted && (
                                  <Badge className="bg-primary/20 text-primary border-primary/30">
                                    En cours
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                {chapter.description}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                +{chapter.xp_reward || 50} XP
                              </p>
                              <div className="flex gap-2 mt-3 flex-wrap">
                                {chapter.lesson_url && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-primary/50 text-primary hover:bg-primary/10"
                                    onClick={() => openLesson(chapter)}
                                    data-testid={`open-lesson-${chapter.id}`}
                                  >
                                    <PlayCircle className="w-4 h-4 mr-1" />
                                    Ouvrir la leçon
                                  </Button>
                                )}
                                {!isCompleted && !isLocked && (
                                  <Button 
                                    size="sm" 
                                    className="bg-green-500 hover:bg-green-600"
                                    onClick={() => completeChapter(chapter.id)}
                                    disabled={quizLoading}
                                    data-testid={`complete-chapter-${chapter.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Marquer comme complété
                                  </Button>
                                )}
                                {isCompleted && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                                    onClick={() => uncompleteChapter(chapter.id)}
                                    data-testid={`uncomplete-chapter-${chapter.id}`}
                                  >
                                    <Circle className="w-4 h-4 mr-1" />
                                    Annuler
                                  </Button>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Milestones */}
          <div>
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Jalons personnels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-3">
                    {milestoneDefinitions.map((milestone, index) => {
                      const userMs = userMilestones.find(m => m.milestone_id === milestone.id);
                      const achieved = userMs?.completed;
                      const claimStatus = userMs?.claim_status;
                      const pending = claimStatus === 'pending_coach' || claimStatus === 'pending_ai';
                      const rejected = claimStatus === 'rejected';
                      const mode = milestone.verification || 'coach';
                      
                      return (
                        <motion.div
                          key={milestone.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`p-3 rounded-lg border transition-all overflow-visible ${
                            achieved 
                              ? 'bg-amber-500/10 border-amber-500/30' 
                              : pending
                                ? 'bg-secondary/40 border-primary/30'
                                : 'bg-secondary/30 border-border hover:border-primary/30'
                          }`}
                          data-testid={`milestone-${milestone.id}`}
                        >
                          <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                              achieved ? 'bg-amber-500/20' : 'bg-secondary'
                            }`}>
                              {achieved ? <Star className="w-5 h-5 text-amber-500" /> : milestone.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm ${achieved ? 'text-amber-500' : ''}`}>
                                {milestone.title}
                              </p>
                              {milestone.description && (
                                <p className="text-xs text-muted-foreground">{milestone.description}</p>
                              )}
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Zap className="w-3 h-3" />
                                  +{milestone.xp_reward} XP
                                </span>
                                {mode === 'coach' && (
                                  <span className="text-xs flex items-center gap-1 text-muted-foreground" title="Validation par le coach requise">
                                    <ShieldCheck className="w-3 h-3" />
                                    Validation coach
                                  </span>
                                )}
                                {mode === 'ai' && (
                                  <span className="text-xs flex items-center gap-1 text-muted-foreground" title="Vérification automatique par IA">
                                    <Sparkles className="w-3 h-3" />
                                    Vérification IA
                                  </span>
                                )}
                                {pending && (
                                  <Badge variant="outline" className="text-xs gap-1" data-testid={`milestone-pending-${milestone.id}`}>
                                    <Hourglass className="w-3 h-3" />
                                    En attente de validation
                                  </Badge>
                                )}
                                {rejected && (
                                  <Badge variant="outline" className="text-xs gap-1 border-red-500/40 text-red-500" data-testid={`milestone-rejected-${milestone.id}`}>
                                    <XCircle className="w-3 h-3" />
                                    Non validé
                                  </Badge>
                                )}
                              </div>
                              {rejected && userMs?.admin_note && (
                                <p className="text-xs text-red-500/90 mt-1">{userMs.admin_note}</p>
                              )}
                            </div>
                            {achieved ? (
                              mode === 'auto' ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                                  onClick={() => uncompleteMilestone(milestone.id)}
                                  data-testid={`uncomplete-milestone-${milestone.id}`}
                                >
                                  <Circle className="w-4 h-4 mr-1" />
                                  Annuler
                                </Button>
                              ) : (
                                <Badge className="bg-amber-500/20 text-amber-600 gap-1" data-testid={`milestone-validated-${milestone.id}`}>
                                  <ShieldCheck className="w-3.5 h-3.5" />
                                  Validé
                                </Badge>
                              )
                            ) : pending ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => uncompleteMilestone(milestone.id)}
                                data-testid={`cancel-milestone-${milestone.id}`}
                              >
                                Annuler
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                className="bg-amber-500 hover:bg-amber-600 text-white"
                                onClick={() => completeMilestone(milestone)}
                                data-testid={`complete-milestone-${milestone.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                {mode === 'auto' ? 'Atteint' : 'Déclarer'}
                              </Button>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Tips Card */}
            <Card className="mt-4 border-0 shadow-lg bg-gradient-to-br from-blue-500/10 to-background">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Conseil du jour</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Concentre-toi sur un chapitre à la fois. La régularité est la clé du succès en immobilier !
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Milestone declaration (AI screening + coach validation) */}
      <Dialog
        open={declareDialog.open}
        onOpenChange={(open) => setDeclareDialog({ open, milestone: open ? declareDialog.milestone : null })}
      >
        <DialogContent className="max-w-lg" data-testid="milestone-declare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <ShieldCheck className="w-5 h-5 text-primary" />
              Déclarer : {declareDialog.milestone?.title}
            </DialogTitle>
            <DialogDescription>
              {(declareDialog.milestone?.verification || 'coach') === 'coach'
                ? `Ce jalon vaut ${declareDialog.milestone?.xp_reward} XP et est validé par le coach. Détaille ton accomplissement : dates, montants, interlocuteurs, ville.`
                : `Ce jalon vaut ${declareDialog.milestone?.xp_reward} XP. Ta déclaration est vérifiée automatiquement : sois précis et concret.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Textarea
              value={proofText}
              onChange={(e) => setProofText(e.target.value)}
              rows={7}
              placeholder="Ex : Le 12 mai, visite d'un T2 de 42 m² rue Gambetta à Rouen, annoncé à 96 000 €. Offre à 88 500 € via l'agence Century 21 (Mme Léger), acceptée le 19 mai. Financement en cours au Crédit Agricole..."
              data-testid="milestone-proof-input"
            />
            <div className="flex items-center justify-between text-xs">
              <span className={proofText.trim().length < 80 ? 'text-muted-foreground' : 'text-primary'}>
                {proofText.trim().length}/80 caractères minimum
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Sparkles className="w-3 h-3" />
                Analyse assistée par IA
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Une déclaration inexacte peut entraîner le retrait des XP et des récompenses associées.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclareDialog({ open: false, milestone: null })}>
              Annuler
            </Button>
            <Button onClick={submitDeclaration} disabled={declaring} data-testid="milestone-declare-submit">
              {declaring ? 'Vérification...' : 'Envoyer la déclaration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI reading-control question before validating a chapter */}
      <Dialog
        open={quizDialog.open}
        onOpenChange={(open) => setQuizDialog({ open, chapterId: open ? quizDialog.chapterId : null, quiz: open ? quizDialog.quiz : null })}
      >
        <DialogContent className="max-w-lg" data-testid="chapter-quiz-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Sparkles className="w-5 h-5 text-primary" />
              Question de contrôle
            </DialogTitle>
            <DialogDescription>
              Réponds correctement pour valider le chapitre et encaisser l'XP. Question générée par IA à partir du contenu du chapitre.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <p className="font-medium text-sm" data-testid="chapter-quiz-question">{quizDialog.quiz?.question}</p>
            <div className="space-y-2">
              {quizDialog.quiz?.options?.map((option, index) => (
                <button
                  key={index}
                  onClick={() => setQuizAnswer(index)}
                  className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${
                    quizAnswer === index
                      ? 'border-primary bg-primary/10 font-medium'
                      : 'border-border hover:border-primary/40'
                  }`}
                  data-testid={`chapter-quiz-option-${index}`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQuizDialog({ open: false, chapterId: null, quiz: null })}>
              Plus tard
            </Button>
            <Button onClick={submitQuiz} disabled={quizLoading} data-testid="chapter-quiz-submit">
              {quizLoading ? 'Vérification...' : 'Valider ma réponse'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgressionPage;
